import { useMemo } from 'react';

import { dontUpsell } from '@trello/browser';
import { useMemberNodeId } from '@trello/business-logic-react/member';
import { getFreeTrialProperties } from '@trello/business-logic/organization';
import { Entitlements } from '@trello/entitlements';

import { hasAlreadyUsedTrial } from 'app/src/components/FreeTrial/useStartFreeTrialForOrg';
import {
  useEligibleWorkspacesToUpgradeQuery,
  type EligibleWorkspaceToUpgrade,
} from 'app/src/components/SelectWorkspaceToUpgrade';
import { usePlannerWorkspacePremiumFeaturesFragment } from './PlannerWorkspacePremiumFeaturesFragment.generated';
import { usePlannerFeatureGate } from './usePlannerFeatureGate';

const eligibilityFilter = (workspace: EligibleWorkspaceToUpgrade) =>
  Entitlements.isFree(workspace.offering);

const isEligibleForTrial = (workspace: EligibleWorkspaceToUpgrade) => {
  return !hasAlreadyUsedTrial(workspace.credits ?? []);
};

/**
 * This function is used to determine if we should place a user in the freeTrial cohort.
 * It returns true if the following conditions are met:
 *   - every workspace is either free or on a free trial
 *   - there is at least one workspace with a free trial
 */

const areAllWorkspacesUnpaidWithAtLeastOneTrial = (
  workspaces: EligibleWorkspaceToUpgrade[],
) => {
  let hasWorkspaceWithTrial = false;
  for (const workspace of workspaces) {
    const freeTrialProperties = getFreeTrialProperties(
      workspace.credits || [],
      workspace.offering,
    );
    const isFreeTrialActive = freeTrialProperties?.isActive;
    if (isFreeTrialActive) {
      hasWorkspaceWithTrial = true;
    }
    if (!Entitlements.isFree(workspace.offering) && !isFreeTrialActive) {
      return false;
    }
  }
  // If we haven't returned early yet, then all workspaces are either free or on a trial.
  // Because of this, simply return whether or not we've seen at least one trial.
  return hasWorkspaceWithTrial;
};

export type UpsellCohort =
  | 'cohortLoading'
  | 'freeTrial'
  | 'noUpgradeAvailable'
  | 'paidUser'
  | 'trialAvailable'
  | 'upgradeAvailable'
  | 'upgradeAvailableWithoutUpsell';

/**
 * Hook to determine the upsell cohort and eligibility for a user in the Planner.
 *
 * This hook evaluates the user's workspace status and eligibility for different upsell paths:
 * - Checks if the user has premium Planner features
 * - Determines trial eligibility for free workspaces
 * - Handles cases where upsells should not be shown
 *
 * @returns {Object} An object containing:
 *   - upsellCohort: The determined cohort ('cohortLoading' | 'freeTrial' | 'noUpgradeAvailable' | 'paidUser' | 'trialAvailable' | 'upgradeAvailable' | 'upgradeAvailableWithoutUpsell')
 *
 * @example
 * const { upsellCohort } = useUpsellData();
 * if (upsellCohort === 'trialAvailable') {
 *   // Show trial upsell
 * }
 */
export const useUpsellData: () => {
  upsellCohort: UpsellCohort;
} = () => {
  const memberNodeId = useMemberNodeId();
  const { isMultiAccountM2Enabled } = usePlannerFeatureGate();

  const { data } = usePlannerWorkspacePremiumFeaturesFragment({
    from: { id: memberNodeId },
    optimistic: true,
  });

  const {
    workspaces,
    allWorkspaces,
    loading: workspacesLoading,
  } = useEligibleWorkspacesToUpgradeQuery({
    filter: eligibilityFilter,
  });

  const upsellCohort: UpsellCohort = useMemo(() => {
    if (workspacesLoading) {
      return 'cohortLoading';
    }
    if (data?.planner?.workspace) {
      const hasAdvancedPlanner =
        data.planner.workspace.premiumFeatures?.includes('advancedPlanner');

      if (hasAdvancedPlanner) {
        if (
          isMultiAccountM2Enabled &&
          areAllWorkspacesUnpaidWithAtLeastOneTrial(allWorkspaces)
        ) {
          return 'freeTrial';
        } else {
          return 'paidUser';
        }
      } else {
        if (workspaces.some(isEligibleForTrial)) {
          return 'trialAvailable';
        } else if (dontUpsell()) {
          // There are some situations where we want to indicate to the user that they don't have the full
          // Planner experience, but we are not allowed to explicitly show them upsells (e.g. desktop).
          return 'upgradeAvailableWithoutUpsell';
        } else if (!workspaces.length) {
          // If the user joins Trello via a board invite link they won't have any workspaces available
          // that they can upgrade.
          return 'noUpgradeAvailable';
        } else {
          return 'upgradeAvailable';
        }
      }
    } else {
      return 'noUpgradeAvailable';
    }
  }, [
    allWorkspaces,
    data?.planner?.workspace,
    isMultiAccountM2Enabled,
    workspaces,
    workspacesLoading,
  ]);

  return { upsellCohort };
};
